// Query options for the operations screens (add-ons, failed events, the cross-company DE list,
// the cost ledger, a report's detail): kept beside `queries.ts` so its keys and budgets stay
// readable. Keys nest under the same roots (`["operator", …]`, `["company", co, …]`), so the
// existing invalidations still reach them.
import { queryOptions } from "@tanstack/react-query";
import type { Api } from "../api/client";

export const opsKeys = {
  entitlements: (companyId: string) => ["operator", "entitlements", companyId] as const,
  operatorDes: (week: string) => ["operator", "digital-employees", week] as const,
  operatorSystemAgents: () => ["operator", "system-agents"] as const,
  systemAgentSpace: (co: string, id: string) =>
    ["operator", "system-agents", co, id, "space"] as const,
  agentHealth: () => ["operator", "agent-health"] as const,
  agentHealthList: (filters: AgentHealthFilters) =>
    ["operator", "agent-health", "list", filters] as const,
  agentHealthTopic: (co: string) => ["operator", "agent-health", "topic", co] as const,
  opportunities: () => ["operator", "opportunities"] as const,
  opportunitiesList: (filters: OpportunityFilters) =>
    ["operator", "opportunities", "list", filters] as const,
  opportunity: (co: string, id: string) => ["operator", "opportunities", "detail", co, id] as const,
  // SKILL-PROPOSALS-OPERATOR: the operator-only skill-proposal queue (super admins)
  skillProposals: () => ["operator", "skill-proposals"] as const,
  skillProposalsList: (status: string) => ["operator", "skill-proposals", "list", status] as const,
  skillProposal: (co: string, id: string) =>
    ["operator", "skill-proposals", "detail", co, id] as const,
  costs: (co: string, month: string, visibility: string) =>
    ["company", co, "costs", month, visibility] as const,
  costsAll: (co: string) => ["company", co, "costs"] as const,
  report: (co: string, id: string) => ["company", co, "reports", "detail", id] as const,
};

export const entitlementsQuery = (api: Api, companyId: string) =>
  queryOptions({
    queryKey: opsKeys.entitlements(companyId),
    queryFn: () => api.client.operator.entitlements.get({ params: { company_id: companyId } }),
  });

export const operatorDesQuery = (api: Api, week: string) =>
  queryOptions({
    queryKey: opsKeys.operatorDes(week),
    queryFn: () => api.client.operator.digitalEmployees.list({ query: { week } }),
  });

/** SYSTEM-AGENT: every system agent of the granted companies, with its latest reports. */
export const operatorSystemAgentsQuery = (api: Api) =>
  queryOptions({
    queryKey: opsKeys.operatorSystemAgents(),
    queryFn: () => api.client.operator.systemAgents.list(),
  });

/** SYSTEM-AGENT: a super admin's private space with one system agent. */
export const systemAgentSpaceQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: opsKeys.systemAgentSpace(co, id),
    queryFn: () =>
      api.client.operator.systemAgents.space.get({
        params: { company_id: co, membership_id: id },
      }),
  });

/** SKILLSMITH-HUB: the Agent health morning view's filters (all optional; API defaults). */
export type AgentHealthFilters = {
  severity?: string;
  company_id?: string;
  status?: "unreviewed" | "reviewed" | "all";
};

/** SKILLSMITH-HUB: every granted company's new system-agent reports, counts and the inbox. */
export const agentHealthQuery = (api: Api, filters: AgentHealthFilters = {}) =>
  queryOptions({
    queryKey: opsKeys.agentHealthList(filters),
    queryFn: () => api.client.operator.agentHealth.get({ query: filters }),
  });

/** SKILLSMITH-HUB: one company's topic (reports and messages, oldest first). */
export const agentHealthTopicQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: opsKeys.agentHealthTopic(co),
    queryFn: () => api.client.operator.agentHealth.topic({ params: { company_id: co } }),
  });

/** SKILLSMITH-OPPORTUNITIES: the pipeline's filters (all optional). */
export type OpportunityFilters = {
  company_id?: string;
  stage?: "suggested" | "offered" | "accepted" | "declined" | "added" | "dismissed";
};

/** SKILLSMITH-OPPORTUNITIES: the helper sales pipeline (counts, revenue, the opportunities). */
export const opportunitiesQuery = (api: Api, filters: OpportunityFilters = {}) =>
  queryOptions({
    queryKey: opsKeys.opportunitiesList(filters),
    queryFn: () => api.client.operator.opportunities.list({ query: filters }),
  });

/** SKILLSMITH-OPPORTUNITIES: one opportunity with the company's people (offer recipients). */
export const opportunityQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: opsKeys.opportunity(co, id),
    queryFn: () =>
      api.client.operator.opportunities.get({ params: { company_id: co, opportunity_id: id } }),
  });

/** SKILL-PROPOSALS-OPERATOR: every granted company's skill proposals (super admins only). */
export const operatorSkillProposalsQuery = (api: Api, status: string) =>
  queryOptions({
    queryKey: opsKeys.skillProposalsList(status),
    queryFn: () =>
      api.client.operator.skillProposals.list({
        query:
          status === "all"
            ? {}
            : { status: status as "proposed" | "ready" | "approved" | "rejected" | "shipped" },
      }),
    meta: { quietStatuses: [404] },
    retry: false,
  });

export const operatorSkillProposalQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: opsKeys.skillProposal(co, id),
    queryFn: () =>
      api.client.operator.skillProposals.get({ params: { company_id: co, proposal_id: id } }),
    meta: { quietStatuses: [404] },
    retry: false,
  });

/** One month of the ledger (`period` = YYYY-MM), newest first, cursor-paged on request. */
export const COST_PAGE = 50;

export const reportDetailQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: opsKeys.report(co, id),
    queryFn: () => api.client.reports.get({ params: { co, id } }),
  });

/** Today's date in UTC, the day the API compares `starts_on` against. */
export function todayUtc(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}
